import React, { useCallback, useContext, useState } from "react";
import ReactGA from "react-ga4";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  CommissionFee,
  ConfirmationModal,
  NavigationBlock,
  ParameterInput,
  ParametersInputFields,
  ServiceCartSelect,
  ServiceErrorModal,
  StyledColumn,
  StyledContent,
  StyledForm,
  ViewportContext,
} from "@monorepo/components";
import { handleUpdateConflictingServiceUpdateError } from "@monorepo/components/src/ServiceForms/helpers/serviceErrorHandler";
import { routeConfig } from "@monorepo/constants/routeConfig";
import {
  countersRegex,
  findErrorByParam,
  getTerminalError,
  getTotalSum,
  isCounterWarning,
  isResponseErrorArray,
  isResponseWithSourceErrorArray,
  mapDefaultValues,
  modifyNullParams,
  showErrorArrayTitles,
} from "@monorepo/helpers";
import { useCalculateAmount } from "@monorepo/hooks";
import { useDeclaration } from "@monorepo/hooks/src/useDeclaration";
import { useUpdateConflictingService } from "@monorepo/hooks/src/useUpdateConflictingService";
import { useClientInfoQuery } from "@monorepo/services/api/auth";
import { useAddServiceToCartMutation } from "@monorepo/services/api/carts";
import { useCreateOrderMutation, useLazyGetActiveOrderQuery } from "@monorepo/services/api/order";
import theme from "@monorepo/styles/theme";
import {
  ErrorCodeEnum,
  FormStepsEnum,
  IActiveCartSliceState,
  IResponseError,
  IService,
  ServiceParameterEnum,
  SubmitTypeEnum,
} from "@monorepo/types";
import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import { reverse } from "named-urls";

import ServiceListAutocomplete from "components/ServiceListAutocomplete/ServiceListAutocomplete";

// import { handleUpdateConflictingServiceUpdateError } from "./helpers/serviceErrorHandler";

interface IFormData {
  cart_group_id: string;
  [key: string]: string;
}

interface IProps {
  service: IService;
  templateId?: string;
  isPaymentBankOnly?: boolean;
}

export const ServiceFieldsForm: React.FC<IProps> = ({ service, templateId, isPaymentBankOnly }) => {
  const activeCartId = useSelector(({ activeCartSlice }: IActiveCartSliceState) => activeCartSlice.cartId);
  const parameters = service.parameters;
  const methods = useForm<IFormData>({
    defaultValues: mapDefaultValues(parameters, {
      cart_group_id: activeCartId,
    }),
  });

  const location = useLocation();
  const [addServiceToCart, { isLoading }] = useAddServiceToCartMutation();
  const [getActiveOrder] = useLazyGetActiveOrderQuery();
  const [serviceError, setServiceError] = useState<IResponseError>();
  const [isDeclarationInitiated, setIsDeclarationInitiated] = useState<boolean>(false);
  const [declarationIsLoading, setDeclarationIsLoading] = useState<boolean>(false);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [inputErrors, setInputErrors] = useState<Array<IResponseError>>();
  const [createOrder, { isLoading: createOrderIsLoading }] = useCreateOrderMutation();
  const { t } = useTranslation();
  const history = useHistory();
  const [submitAction, setSubmitAction] = useState<SubmitTypeEnum>(SubmitTypeEnum.PAYNOW);
  const [counterWarning, setCounterWarning] = useState<string | undefined>();
  const { data: clientInfo, isFetching } = useClientInfoQuery({ skipInitialLoad: true });
  const { isThirdParty } = useContext(ViewportContext);
  const isThirdPartyPE = !!(isThirdParty && service.id === String(clientInfo?.perlas_energija_service_id));
  const { handleDeclarationConflictResolutionSuccess } = useDeclaration();
  const [action, setAction] = useState<() => void>();

  const { updateConflictingService } = useUpdateConflictingService({
    serviceId: service.id,
    serviceName: service.api_service_name,
    onSuccess: ({ res }) => handleDeclarationConflictResolutionSuccess({ res, service }),
    onError: (err) => handleUpdateConflictingServiceUpdateError({ err, service, setShowErrorModal, setInputErrors }),
    params: methods.getValues(),
  });

  const {
    amount,
    handleCalculate,
    isLoading: isCalculateLoading,
  } = useCalculateAmount({
    serviceId: service.id,
    methods,
    counters: parameters,
    isThirdPartyPE,
    id: service.id,
  });

  const handlePayNow = useCallback(
    async (data: IFormData) => {
      toast.dismiss();
      const { cart_group_id: cartGroupId, ...params } = data;
      const path = routeConfig.dashboard.payments.paymentOverview;
      addServiceToCart({
        cart_group_id: cartGroupId,
        service_id: service.id,
        service_name: service.api_service_name,
        params: modifyNullParams(params),
        pay_now: true,
        paid: false,
      })
        .unwrap()
        .then((res) => {
          createOrder({
            order_sum: String(getTotalSum(params)),
            cart_count: "1",
            data_declaration: false,
            cart_id: [Number(res.data.id)],
          })
            .unwrap()
            .then(() => {
              ReactGA.event("pay_now", {
                service_id: service.id,
              });

              getActiveOrder()
                .unwrap()
                .then((activeOrderRes) => {
                  if (activeOrderRes) {
                    setAction(
                      () => () =>
                        history.push(path, {
                          isPaymentBankOnly,
                        })
                    );
                  }
                });
            })
            .catch((err) => {
              showErrorArrayTitles(err);
            });
        })
        .catch((err) => {
          const terminalError = getTerminalError(err);
          if (!!terminalError) {
            toast.error(terminalError);
          } else if (err) {
            showErrorArrayTitles(err);
          }
          if (isResponseWithSourceErrorArray(err)) {
            setServiceError(err.filter((x) => !countersRegex.test(x.source.parameter!))[0]);
            setShowErrorModal(true);
            setInputErrors(err.filter(({ source }) => source?.parameter));
          }
        });
    },
    [addServiceToCart, createOrder, getActiveOrder, history, service.api_service_name, service.id]
  );

  const handleDeclare = useCallback(
    async (data: IFormData) => {
      setDeclarationIsLoading(true);
      toast.dismiss();
      const { cart_group_id: cartGroupId, ...params } = data;
      const path = routeConfig.dashboard.payments.paymentOverview;
      addServiceToCart({
        cart_group_id: cartGroupId,
        service_id: service.id,
        service_name: service.api_service_name,
        data_declaration: true,
        params: modifyNullParams(params),
        pay_now: false,
        paid: false,
      })
        .unwrap()
        .then((res) => {
          createOrder({
            order_sum: String(res.data.attributes.sum),
            cart_count: "1",
            data_declaration: true,
            cart_id: [Number(res.data.id)],
          })
            .unwrap()
            .then(() => {
              ReactGA.event("declaration", {
                service_id: service.id,
              });

              getActiveOrder()
                .unwrap()
                .then((activeOrderRes) => {
                  if (activeOrderRes) {
                    setAction(
                      () => () =>
                        history.push(path, {
                          isPaymentBankOnly,
                        })
                    );
                  }
                });
            })
            .catch((err) => {
              showErrorArrayTitles(err);
            });
        })
        .catch((err) => {
          const terminalError = getTerminalError(err);
          if (!!terminalError) {
            toast.error(terminalError);
          } else if (err) {
            if (Number(err[0].status) !== ErrorCodeEnum.CONFLICT) {
              showErrorArrayTitles(err);
            }
          }
          if (isResponseWithSourceErrorArray(err)) {
            updateConflictingService({
              dataDeclaration: true,
              error: err.filter((x) => !countersRegex.test(x.source.parameter!))[0],
            });
            setInputErrors(err.filter(({ source }) => source?.parameter));
          }
        })
        .finally(() => {
          setDeclarationIsLoading(false);
        });
    },
    [
      addServiceToCart,
      createOrder,
      getActiveOrder,
      history,
      service.api_service_name,
      service.id,
      updateConflictingService,
    ]
  );

  const onSubmit = useCallback(
    async (data: IFormData) => {
      toast.dismiss();
      const { cart_group_id: cartGroupId, ...params } = data;
      const path = reverse(routeConfig.dashboard.payments.cartGroup.self, {
        cartGroupId,
      });

      addServiceToCart({
        cart_group_id: cartGroupId,
        service_id: service.id,
        service_name: service.api_service_name,
        params: modifyNullParams(params),
        pay_now: false,
        paid: false,
        delete_template_id: templateId ? templateId : undefined,
      })
        .unwrap()
        .then((res) => {
          ReactGA.event("add_to_list", {
            service_id: service.id,
          });
          toast.success(t("common.service_succesfully_added"), { toastId: service.id });

          return Promise.resolve(res);
        })
        .then((res) => {
          return Promise.resolve(res);
        })
        .then(() => {
          if (templateId) {
            toast.success(t("services.template_deleted"));
          }

          setAction(
            () => () => history.push({ pathname: path, state: { from: location.pathname, forceShowModal: true } })
          );
        })
        .catch((err) => {
          const terminalError = getTerminalError(err);
          if (!!terminalError) {
            toast.error(terminalError);
          }
          if (isResponseErrorArray(err)) {
            setServiceError(err[0]);
            setShowErrorModal(true);

            if (isResponseWithSourceErrorArray(err)) {
              setInputErrors(err.filter(({ source }) => source?.parameter));
            }
          }
        });
    },
    [addServiceToCart, history, location.pathname, service.api_service_name, service.id, templateId, t]
  );

  const handleConfirmationModalAction = useCallback(() => {
    setCounterWarning(undefined);
    if (submitAction === SubmitTypeEnum.PAYNOW) {
      methods.handleSubmit(handlePayNow)();
    } else if (submitAction === SubmitTypeEnum.ADD_TO_CART) {
      methods.handleSubmit(onSubmit)();
    } else if (submitAction === SubmitTypeEnum.DECLARE) {
      methods.handleSubmit(handleDeclare)();
    }
  }, [handleDeclare, handlePayNow, methods, onSubmit, submitAction]);

  if (parameters?.length && !isFetching) {
    const firstStepParameter = parameters?.find(({ step }) => step === FormStepsEnum.ACCOUNT_VERIFICATION);
    const secondStepParameters = parameters?.filter(({ step }) => step === FormStepsEnum.SERVICE_FIELDS);
    return (
      <>
        <NavigationBlock methods={methods} action={action} setAction={setAction} />
        <FormProvider {...methods}>
          <StyledForm onSubmit={methods.handleSubmit(onSubmit)}>
            <StyledContent>
              <StyledColumn>
                <ServiceCartSelect fieldName={"cart_group_id"} defaultValue={activeCartId} />
                <div>
                  <ServiceListAutocomplete disabled initialValue={service.api_service_name} />
                </div>
                {firstStepParameter && (
                  <ParameterInput
                    parameter={firstStepParameter}
                    error={findErrorByParam({ param: firstStepParameter.parameter_name, error: inputErrors })}
                  />
                )}
                <ParametersInputFields
                  control={methods.control}
                  calculateError={methods.formState.errors.calculateError}
                  amount={amount}
                  isCalculateLoading={isCalculateLoading}
                  onCalculate={handleCalculate}
                  amtCalculable={service.amt_calculation}
                  inputErrors={inputErrors || []}
                  parameters={secondStepParameters.filter(
                    (p) =>
                      p.parameter_name === ServiceParameterEnum.CUSNO || p.parameter_name === ServiceParameterEnum.A1
                  )}
                />
              </StyledColumn>

              <StyledColumn>
                <ParametersInputFields
                  control={methods.control}
                  calculateError={methods.formState.errors.calculateError}
                  amount={amount}
                  isCalculateLoading={isCalculateLoading}
                  onCalculate={handleCalculate}
                  amtCalculable={service.amt_calculation}
                  parameters={secondStepParameters.filter(
                    (p) =>
                      p.parameter_name !== ServiceParameterEnum.CUSNO && p.parameter_name !== ServiceParameterEnum.A1
                  )}
                  inputErrors={inputErrors || []}
                />

                {service.commission && <CommissionFee commission={service.commission} />}
              </StyledColumn>
            </StyledContent>
            <Box
              sx={{
                marginTop: theme.spacing(2),
                display: "flex",
                flexDirection: {
                  xs: "column",
                  sm: "row",
                },
                width: {
                  xs: "100%",
                  sm: "auto",
                },
                justifyContent: "center",
                columnGap: theme.spacing(3),
                rowGap: theme.spacing(2),
              }}
            >
              <LoadingButton
                loading={isLoading || declarationIsLoading || createOrderIsLoading || methods.formState.isSubmitting}
                disabled={isLoading || declarationIsLoading || createOrderIsLoading || methods.formState.isSubmitting}
                disableElevation
                variant="contained"
                color="secondary"
                onClick={(e) => {
                  setSubmitAction(SubmitTypeEnum.PAYNOW);
                  if (!isCounterWarning(methods.getValues(), parameters)) {
                    methods.handleSubmit(handlePayNow)(e);
                  } else {
                    setShowErrorModal(true);
                    setCounterWarning(t("payments.counter_warning"));
                  }
                }}
              >
                {t("cart.pay_now")}
              </LoadingButton>
              {service.data_declaration && (
                <LoadingButton
                  loading={isLoading || declarationIsLoading || createOrderIsLoading || methods.formState.isSubmitting}
                  disabled={isLoading || declarationIsLoading || createOrderIsLoading || methods.formState.isSubmitting}
                  disableElevation
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    setSubmitAction(SubmitTypeEnum.DECLARE);
                    if (!isCounterWarning(methods.getValues(), parameters)) {
                      setIsDeclarationInitiated(true);
                    } else {
                      setShowErrorModal(true);
                      setCounterWarning(t("payments.counter_warning"));
                    }
                  }}
                >
                  {t("common.to_declare")}
                </LoadingButton>
              )}
              <LoadingButton
                loading={
                  isLoading ||
                  declarationIsLoading ||
                  createOrderIsLoading ||
                  isCalculateLoading ||
                  methods.formState.isSubmitting
                }
                disabled={
                  isLoading ||
                  declarationIsLoading ||
                  createOrderIsLoading ||
                  isCalculateLoading ||
                  methods.formState.isSubmitting
                }
                disableElevation
                onClick={(e) => {
                  setSubmitAction(SubmitTypeEnum.ADD_TO_CART);
                  if (!isCounterWarning(methods.getValues(), parameters)) {
                    methods.handleSubmit(onSubmit)(e);
                  } else {
                    setShowErrorModal(true);
                    setCounterWarning(t("payments.counter_warning"));
                  }
                }}
                variant="contained"
              >
                {t("cart.save")}
              </LoadingButton>
            </Box>
          </StyledForm>
        </FormProvider>
        {counterWarning && showErrorModal && (
          <ConfirmationModal
            open={showErrorModal}
            onAction={() => {
              handleConfirmationModalAction();
              setCounterWarning(undefined);
              setShowErrorModal(false);
            }}
            onClose={() => {
              setCounterWarning(undefined);
              setShowErrorModal(false);
            }}
            declineButtonText={t("common.view")}
            confirmButtonText={t("common.confirm")}
          >
            {counterWarning}
          </ConfirmationModal>
        )}
        {isDeclarationInitiated && !counterWarning && (
          <ConfirmationModal
            open={isDeclarationInitiated}
            onAction={() => {
              handleConfirmationModalAction();
              setCounterWarning(undefined);
              setIsDeclarationInitiated(false);
            }}
            onClose={() => {
              setCounterWarning(undefined);
              setIsDeclarationInitiated(false);
            }}
            declineButtonText={t("common.cancel")}
            confirmButtonText={t("common.to_declare")}
          >
            {t("cart.modal.declaration")}
          </ConfirmationModal>
        )}
        {showErrorModal && serviceError && !counterWarning && (
          <ServiceErrorModal
            open={showErrorModal}
            serviceId={service.id}
            error={serviceError}
            serviceName={service.api_service_name}
            onSuccess={({ cartGroupId }) => {
              ReactGA.event("add_to_list", { service_id: service.id });
              toast.success(t("common.service_succesfully_added"), {
                toastId: service.id,
              });

              if (!cartGroupId) {
                return null;
              }

              setAction(
                () => () =>
                  history.push({
                    pathname: reverse(routeConfig.dashboard.payments.cartGroup.self, {
                      cartGroupId: cartGroupId,
                    }),
                    state: { from: location.pathname, forceShowModal: true },
                  })
              );
            }}
            onError={(err) => {
              const terminalError = getTerminalError(err);
              if (!!terminalError) {
                toast.error(terminalError);
              } else {
                err.forEach(({ source, title }) => {
                  if (!source?.parameter) {
                    toast.error(title, {
                      toastId: service.id,
                    });
                  }
                });
              }
              setInputErrors(err.filter(({ source }) => source?.parameter));
              setShowErrorModal(false);
            }}
            onModalClose={() => setShowErrorModal(false)}
            params={methods.getValues()}
          />
        )}
      </>
    );
  }
  return null;
};

export default ServiceFieldsForm;
